/**
 * Central route-metadata map for SEO tags.
 *
 * Key = canonical path (no locale prefix). For `/de/*` and `/ja/*` routes, the
 * SEORouter strips the locale segment before lookup so English/German/Japanese
 * public pages share the same title/description template.
 *
 * Every public marketing route below MUST also appear in
 * `backend/server.py :: STATIC_ROUTES` (which drives /api/sitemap.xml).
 */

export const SITE_ORIGIN = "https://spiruva.com";

// Absolute default OG image URL. Falls back to the same file if not shipped.
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-default.png`;

// Organization schema — attached to homepage
export const ORGANIZATION_JSONLD = {
  "@type": "Organization",
  "@id": `${SITE_ORIGIN}/#organization`,
  name: "SPIRUVA",
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/logo.png`,
  description:
    "Premium phycocyanin (E18 · E25 · E30 · E40) and certified organic spirulina platform. India-grown, science-graded, preparing for July 2027 global launch.",
  foundingLocation: {
    "@type": "Country",
    name: "India",
  },
  sameAs: ["https://linkedin.com/company/spiruva"],
  contactPoint: [
    {
      "@type": "ContactPoint",
      email: "export@spiruva.com",
      contactType: "Sales",
      areaServed: ["EU", "JP", "US", "AE", "KR", "AU", "IN"],
      availableLanguage: ["en", "de", "ja"],
    },
  ],
};

const WEBSITE_JSONLD = {
  "@type": "WebSite",
  "@id": `${SITE_ORIGIN}/#website`,
  url: SITE_ORIGIN,
  name: "SPIRUVA",
  publisher: { "@id": `${SITE_ORIGIN}/#organization` },
  inLanguage: ["en", "de", "ja"],
};

/**
 * Per-route metadata. Order matches STATIC_ROUTES in the sitemap.
 * If a route is not listed here, SEORouter falls back to a sensible default
 * (site-wide title + description) so a page never ships with an empty <title>.
 */
export const ROUTE_META = {
  "/": {
    title: "SPIRUVA — Nature's Purest Blue. Prepared for Global Launch.",
    description:
      "Pre-launch phycocyanin (E18 · E25 · E30 · E40) and certified organic spirulina platform. India-grown, science-graded, preparing for July 2027. Technical review and partner intake open.",
    jsonLd: [ORGANIZATION_JSONLD, WEBSITE_JSONLD],
  },
  "/products": {
    title: "Phycocyanin Grade Portfolio · E18 · E25 · E30 · E40 — SPIRUVA",
    description:
      "Documented purity ladder from formulation-grade E18 to advanced-technical E40. Spec sheets, applications and grade fit for food, cosmetic, pharma, diagnostics and research buyers.",
  },
  "/science": {
    title: "Science & Quality · Cultivation, Extraction, Purity Control — SPIRUVA",
    description:
      "How SPIRUVA phycocyanin is cultivated in South Indian raceways, extracted, freeze-dried and validated to A620/A280 purity thresholds — every batch documented for global buyers.",
  },
  "/markets": {
    title: "Markets & Applications · 16 Verticals — SPIRUVA Phycocyanin",
    description:
      "Sixteen application briefings across food & beverage, cosmetics, pharma-diagnostics, aquaculture, biofertilizer and space nutrition — grade fit, dosage windows and regulatory pathway.",
  },
  "/about": {
    title: "About · India-Grown, Prepared for Global Launch — SPIRUVA",
    description:
      "SPIRUVA is an initiative of Carbofixers cultivating phycocyanin and organic spirulina in India with 300+ productive days per year, preparing for a July 2027 global launch.",
  },
  "/certifications": {
    title: "Certifications & Compliance Register — SPIRUVA",
    description:
      "Active and in-progress certifications: FSSAI · APEDA · ISO 22000 · HACCP · Halal · Organic pathway · WHO-GMP framework · NABL-grade analytical. The procurement-facing compliance file.",
  },
  "/distributor-portal": {
    title: "Distributor Portal · Apply for 2027 Territory — SPIRUVA",
    description:
      "Country-exclusive distributor territories for phycocyanin and organic spirulina open ahead of July 2027 commercial readiness. Apply for evaluation, allocation planning and first-buyer status.",
  },
  "/resources": {
    title: "Resources · Brochures, Use Cases & Fact Sheets — SPIRUVA",
    description:
      "Download the company brochure, four grade brochures (E18-E40), sixteen industry use cases and three fact sheets used in technical reviews, distributor onboarding and procurement diligence.",
  },
  "/reserve-allocation": {
    title: "Reserve 2027 Allocation · Pre-Launch Intake — SPIRUVA",
    description:
      "Reserve your July 2027 phycocyanin or organic spirulina allocation. Priority slotting by grade, technical review, sampling on approval and early-bird pre-launch pricing for pre-booking partners.",
  },
  "/inquiry": {
    title: "Technical Inquiry · Partner Intake Desk — SPIRUVA",
    description:
      "Reach the SPIRUVA partner intake desk for technical review, grade selection, sampling requests and 2027 allocation planning. Response within one working day.",
  },
  "/preorder": {
    title: "Pre-Orders Now Open · 2027 Allocation — SPIRUVA",
    description:
      "Pre-orders for July 2027 phycocyanin and organic spirulina are now open. Reserve your allocation, request samples and lock in early-bird pre-launch pricing before capacity is filled.",
  },
  "/pre-order": {
    // legacy alias — canonicalize to /preorder
    title: "Pre-Orders Now Open · 2027 Allocation — SPIRUVA",
    description:
      "Pre-orders for July 2027 phycocyanin and organic spirulina are now open. Reserve your allocation, request samples and lock in early-bird pre-launch pricing before capacity is filled.",
    canonicalOverride: "/preorder",
  },
  "/brochure": {
    title: "Company Brochure · Master Overview PDF — SPIRUVA",
    description:
      "SPIRUVA company brochure: who we are, grade architecture (E18-E40), applications across 16 verticals, positioning and 2027 launch plan. Download as PDF.",
  },
  "/privacy": {
    title: "Privacy & Data Policy — SPIRUVA",
    description:
      "How SPIRUVA collects, uses and safeguards personal data across the pre-launch website, PDF downloads, newsletter and inquiry forms. GDPR-aligned. Contact export@spiruva.com to request access or deletion.",
  },
  "/journal": {
    title: "Journal · Phycocyanin & Spirulina Research — SPIRUVA",
    description:
      "Long-form research, market intelligence and formulation notes on phycocyanin and spirulina — updated continuously ahead of SPIRUVA's July 2027 commercial readiness.",
  },
};

/**
 * Routes that should NEVER be indexed. Return { robots: 'noindex,nofollow' }.
 * SEORouter matches by prefix.
 */
export const NOINDEX_PREFIXES = ["/admin"];

/** Fallback used when no route match is found (e.g. legacy paths). */
export const FALLBACK_META = {
  title: "SPIRUVA — Pre-Launch Phycocyanin & Spirulina Platform",
  description:
    "Pre-launch phycocyanin (E18 · E25 · E30 · E40) and certified organic spirulina platform. India-grown, science-graded, preparing for July 2027 global launch.",
};

/**
 * Strip the /de or /ja prefix from a pathname so we can look up canonical
 * metadata. Returns { basePath, locale } where locale is "en" | "de" | "ja".
 */
export function splitLocalePath(pathname) {
  const m = /^\/(de|ja)(\/.*)?$/.exec(pathname || "");
  if (m) {
    return { basePath: m[2] || "/", locale: m[1] };
  }
  return { basePath: pathname || "/", locale: "en" };
}

/**
 * Look up metadata for a pathname. Applies noindex when the path matches an
 * admin prefix and always resolves to a valid object.
 */
export function metaForPath(pathname) {
  if (NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    return {
      title: "SPIRUVA · Admin",
      description: "Internal administration console.",
      robots: "noindex,nofollow",
      skipCanonical: true,
    };
  }

  const { basePath } = splitLocalePath(pathname);

  // dynamic blog post — metadata will be set inside the BlogPost component
  if (/^\/(journal|blog)\/[^/]+$/.test(basePath)) {
    return null;
  }

  // /blog is a legacy alias for /journal
  if (basePath === "/blog") {
    return { ...ROUTE_META["/journal"], canonicalOverride: "/journal" };
  }

  return ROUTE_META[basePath] || FALLBACK_META;
}
